import { useAnnonceDerniereActivite, useToastEnDirect } from "~/composables/useToastEnDirect";
import { nomDuTarif, saisonDuTarifTerminee } from "~/utils/tarifAdhesion";
import type { ComputedRef, Ref } from "vue";
import type {
  MembershipPlanPublic,
  MembershipPlanOption,
  MembershipCustomField,
} from "~/types/membership";
import { useDemoAccueil, useDonneeDemoAccueil } from "~/composables/useDemoAccueil";

type SiblingPlan = MembershipPlanPublic & {
  options?: MembershipPlanOption[];
  custom_fields?: MembershipCustomField[];
};

/**
 * useMembershipPlanData — fetch SSR + disponibilité + helpers des plans du
 * groupe pour la page publique /a/[slug]. Extrait 1:1 de
 * `pages/a/[slug]/index.vue` (Zone A split >800 lignes).
 *
 * Regroupe :
 *  - fetch du plan (retry transitoire) + gating "Adhésion non disponible"
 *    UNIQUEMENT sur 404 explicite (archived/draft/not_found → vraie raison)
 *  - fetch des plans frères (tarifs du même groupe)
 *  - helpers par-plan (options actives, custom fields, prix proratisé, titre)
 *  - clôture globale du groupe (tous les tarifs `closed`)
 *  - member_count TEMPS RÉEL via WebSocket (room membership:{groupId})
 */
export async function useMembershipPlanData(
  slug: ComputedRef<string> | Ref<string>,
  /**
   * Mode APERÇU du dashboard (`?preview=true`) : demande à l'API d'inclure les
   * plans encore en BROUILLON, sinon l'aperçu affiche « Adhésion non
   * disponible » exactement dans le cas où il sert (Karim 2026-08-30). Parité
   * `useEventPublicData`.
   */
  isPreview?: ComputedRef<boolean> | Ref<boolean>,
) {
  const { t } = useI18n();
  const config = useRuntimeConfig();
  const previewSuffix = isPreview?.value ? "?preview=true" : "";

  // SSR data fetch — retry 2× sur erreur transitoire (timeout, 5xx) pour
  // éviter le flaky "Adhésion non disponible" au premier refresh quand
  // l'API met >2s à répondre (Karim 2026-06-21).
  //
  // ⚠️ Pas de `await` inline ici : les hooks de cycle de vie plus bas
  // doivent être enregistrés de façon SYNCHRONE (avant tout await) pour
  // rester rattachés à l'instance du composant. Les `await` SSR sont faits
  // tout en bas du composable.
  const planAsync = useFetch<{
    success: boolean;
    data: MembershipPlanPublic & {
      options: MembershipPlanOption[];
      custom_fields: MembershipCustomField[];
    };
  }>(
    urlApiPublique(`${config.public.apiBaseUrl}/api/public/memberships/${slug.value}`, {
      preview: isPreview?.value === true,
      demo: useDemoAccueil().estIntegree,
    }),
    {
      // La clé PORTE le mode aperçu : sans lui, la charge SSR mise en cache
      // pour la page publique serait réutilisée dans l'iframe d'aperçu (et
      // inversement), et un brouillon resterait invisible.
      // Démo de la home : clé à part, rendu du serveur repris (cf. useDonneeDemoAccueil).
      ...useDonneeDemoAccueil(`membership-${slug.value}${previewSuffix ? "-preview" : ""}`),
      server: true,
      lazy: false,
      retry: 2,
      retryDelay: 300,
      timeout: 8000,
    },
  );
  const { data: planData, error: fetchError, pending: isLoadingPlan } = planAsync;

  // APERÇU TEMPS RÉEL — le dashboard pousse le brouillon en cours d'édition
  // par postMessage ; il se superpose aux données enregistrées. Inerte hors
  // `?preview=true`. Karim 2026-08-30.
  const draftPreview = useEntityDraftPreview({
    isPreviewMode: computed(() => isPreview?.value === true),
  });

  const plan = computed(() =>
    draftPreview.mergeEntity(planData.value?.data ?? null),
  );
  const planOptions = computed(() => plan.value?.options || []);
  const customFields = computed(() => plan.value?.custom_fields || []);
  const isInitialLoading = computed(() => isLoadingPlan.value && !plan.value);

  // "Adhésion non disponible" UNIQUEMENT sur 404 explicite (entité supprimée/
  // inexistante). Pour les erreurs transitoires (timeout, 5xx, network),
  // on affiche le skeleton et on laisse le client retry. Sans ce gating, un
  // timeout SSR transitoire faisait disparaître la page entière au refresh.
  // Karim 2026-06-21.
  const isEntityMissing = computed(() => {
    if (plan.value) return false;
    // Pas de plan ET pas d'erreur → SSR pas encore résolu, ne PAS montrer l'erreur.
    if (!fetchError.value) return false;
    // 404 explicite seulement.
    const status = (fetchError.value as { statusCode?: number; status?: number }).statusCode
      ?? (fetchError.value as { statusCode?: number; status?: number }).status;
    return status === 404;
  });

  // Raison réelle du 404 exposée par le backend (archived / draft / not_found)
  // → on affiche la vérité au lieu du message générique. Karim 2026-07-02.
  const missingReason = computed<string>(() => {
    const data = (fetchError.value as { data?: { reason?: string } } | null)?.data;
    return data?.reason ?? "not_found";
  });
  const missingCopy = computed(() => {
    switch (missingReason.value) {
      case "archived":
        return {
          // Illustration maison « mains stop » (plus juste que l'icône archive
          // générique) — rendue en <img> par PublicPageErrorState. Karim 2026-07-08.
          icon: "/svg/hands-no.svg",
          title: t("adhesion.useMembershipPlanData.archiveeTitre"),
          message: t("adhesion.useMembershipPlanData.archiveeMessage"),
        };
      case "draft":
        return {
          icon: "i-lucide-clock",
          title: t("adhesion.useMembershipPlanData.pasEncoreOuverteTitre"),
          message: t("adhesion.useMembershipPlanData.pasEncoreOuverteMessage"),
        };
      default:
        return {
          icon: "i-lucide-id-card",
          title: t("adhesion.useMembershipPlanData.introuvableTitre"),
          message: t("adhesion.useMembershipPlanData.introuvableMessage"),
        };
    }
  });

  // Fetch sibling plans
  const siblingAsync = useFetch<{ success: boolean; data: SiblingPlan[] }>(
    `${config.public.apiBaseUrl}/api/public/memberships/${slug.value}/sibling-plans${previewSuffix}`,
    {
      ...useDonneeDemoAccueil(`membership-siblings-${slug.value}${previewSuffix ? "-preview" : ""}`),
      server: true,
      lazy: true,
      retry: 2,
      retryDelay: 300,
      timeout: 8000,
    },
  );
  const { data: siblingData } = siblingAsync;

  const allPlans = computed(() => {
    const siblings = draftPreview.mergeTarifs(siblingData.value?.data || []);
    if (siblings.length > 0) return siblings;
    if (plan.value) return [plan.value];
    return [];
  });

  // eslint-disable-next-line no-restricted-syntax -- liste locale (siblings + plan), pas paginée backend
  const hasMultiplePlans = computed(() => allPlans.value.length > 1);

  /**
   * Titre du GROUPE d'adhésion (sans le nom d'un tarif). Le backend stocke le
   * `title` de chaque tarif comme `"{groupe} — {tarif}"` en multi-tarif
   * (buildTarifTitle, séparateur « — » = espace + tiret cadratin + espace). Sur
   * la page publique, le H1 doit afficher le nom du GROUPE seul — sinon on colle
   * le nom d'UN tarif au titre alors que le groupe en compte plusieurs (bug
   * « Adhésion Standardd — Premier tarif »).
   *
   * On retire le DERNIER segment « — xxx » de chaque titre de tarif (le tarif
   * étant toujours le dernier segment) puis on garde le nom de groupe s'il est
   * COMMUN à tous les tarifs. Robuste même quand `tarif_name` est `null`
   * (donnée incohérente) et quand le nom du groupe contient lui-même un « — ».
   * En mono-tarif (1 titre), on retombe sur ce titre. Karim 2026-07-08.
   */
  const groupTitle = computed<string>(() => {
    const SEP = " — ";
    const stripTarif = (title: string): string => {
      const i = title.lastIndexOf(SEP);
      return i > 0 ? title.slice(0, i).trim() : title.trim();
    };
    const titles = allPlans.value
      .map((p) => (p.title || "").trim())
      .filter(Boolean);
    if (titles.length === 0) return plan.value?.title || "";

    // Un seul tarif visible → on retire quand même un éventuel suffixe « — xxx »
    // (cas où le groupe a d'autres tarifs mais un seul est chargé, ou donnée
    // « Groupe — Tarif » sur un plan isolé).
    if (titles.length === 1) return stripTarif(titles[0]!) || titles[0]!;

    // Multi-tarifs : le nom de groupe = préfixe commun À CONDITION que tous les
    // tarifs donnent le MÊME groupe après retrait du suffixe. Sinon (titres sans
    // base commune) on retombe sur le titre du plan courant, prudent.
    const groups = [...new Set(titles.map(stripTarif))];
    if (groups.length === 1 && groups[0]) return groups[0];
    return plan.value?.title || titles[0]!;
  });

  // Clôture (Karim 2026-06-27) : groupe entièrement clôturé = AUCUN tarif
  // achetable (tous `closed`). On affiche alors la carte globale « inscriptions
  // clôturées » À LA PLACE du wizard. Si ne serait-ce qu'un tarif est `active`,
  // le wizard reste affiché et les tarifs `closed` sont grisés par-tarif.
  // On se base sur la liste complète (`allPlans`) plutôt que sur le seul plan de
  // l'URL : on peut atterrir sur le slug d'un tarif clôturé alors que des frères
  // sont encore ouverts.

  const isAllPlansClosed = computed(
    () =>
      // eslint-disable-next-line no-restricted-syntax -- allPlans = tarifs complets du groupe (liste courte non paginée), pas un total backend
      allPlans.value.length > 0 &&
      allPlans.value.every((p) => p.status === "closed"),
  );

  // Saison terminée pour TOUS les tarifs : carte « Saison terminée » à la
  // place du wizard (même logique que `isAllPlansClosed`). Un seul tarif
  // encore ouvert → wizard, et les tarifs terminés sont grisés.
  const isAllSeasonsEnded = computed(
    () =>
      // eslint-disable-next-line no-restricted-syntax -- allPlans = tarifs complets du groupe (liste courte non paginée), pas un total backend
      allPlans.value.length > 0 &&
      allPlans.value.every((p) => saisonDuTarifTerminee(p)),
  );

  function getPlanOptions(planId: string): MembershipPlanOption[] {
    const p = allPlans.value.find((pl) => pl.id === planId);
    return (p?.options || []).filter((o) => o.is_active);
  }

  function getPlanCustomFields(planId: string): MembershipCustomField[] {
    const p = allPlans.value.find((pl) => pl.id === planId);
    return p?.custom_fields || [];
  }

  const prorateInfo = computed(() => plan.value?.prorate_info || null);
  const hasProrateDiscount = computed(() => prorateInfo.value !== null && prorateInfo.value.proratedPrice < prorateInfo.value.originalPrice);

  function getPlanPrice(planId: string): number {
    const p = allPlans.value.find((pl) => pl.id === planId);
    if (p && p.id === plan.value?.id && prorateInfo.value) {
      return prorateInfo.value.proratedPrice;
    }
    return Number(p?.price || 0);
  }

  function getPlanTitle(planId: string): string {
    const p = allPlans.value.find((pl) => pl.id === planId);
    return nomDuTarif(p);
  }

  // ── TEMPS RÉEL member_count (WebSocket, zéro polling) ──────────────────
  // Avant : refetch HTTP toutes les 30s du plan complet — chaque tick repassait
  // par GET /api/public/memberships/:slug qui INCRÉMENTE view_count → un onglet
  // ouvert gonflait les « vues » de +2/min (284 fausses vues le jour 1 chez MJR).
  // Maintenant : la page rejoint la room publique `membership:{groupId}` et
  // l'API pousse les compteurs à chaque adhésion confirmée/annulée/expirée
  // (même mécanique que les cagnottes/billetterie). Karim 2026-08-04.
  const membershipSocket = useMembershipSocket();
  const { annoncerEnDirect } = useToastEnDirect();
  // Dernière adhésion (moins de 7 jours) annoncée à l'arrivée.
  useAnnonceDerniereActivite(
    () => plan.value?.derniere_activite,
    (_a, recente) => t(recente ? "commun.enDirect.vientDAdherer" : "commun.enDirect.aAdhere"),
    "i-lucide-id-card",
  );
  onMounted(() => {
    // Join dès que le plan est connu — y compris s'il arrive APRÈS le mount
    // (retry client suite à une erreur SSR transitoire). `immediate` couvre le
    // cas nominal où le plan est déjà là. Join unique (flag — PAS stop() dans
    // le callback : avec `immediate` il s'exécute avant que watch() retourne).
    let joined = false;
    watch(
      () => plan.value?.plan_group_id || plan.value?.id,
      (groupId) => {
        if (!groupId || joined) return;
        joined = true;
        membershipSocket.joinMembershipRoom(groupId);
      },
      { immediate: true },
    );
    membershipSocket.connect();
    membershipSocket.onMembershipUpdate((update) => {
      const countById = new Map(
        update.plans.map((p) => [p.id, p.member_count]),
      );
      // Message en direct : adhérents en PLUS sur les tarifs affichés (le
      // socket pousse aussi les baisses — annulation, expiration — qu'on
      // n'annonce pas). Karim 2026-10-10.
      const affiches = new Map<string, number>();
      if (planData.value?.data) {
        affiches.set(planData.value.data.id, planData.value.data.member_count || 0);
      }
      for (const sibling of siblingData.value?.data ?? []) {
        affiches.set(sibling.id, sibling.member_count || 0);
      }
      let nouveaux = 0;
      for (const [id, avant] of affiches) {
        const apres = countById.get(id);
        if (apres !== undefined && apres > avant) nouveaux += apres - avant;
      }
      if (nouveaux > 0) {
        annoncerEnDirect(
          nouveaux > 1
            ? t("commun.enDirect.viennentDAdherer", { n: nouveaux })
            : t("commun.enDirect.vientDAdherer"),
          "i-lucide-id-card",
        );
      }
      // Plan courant
      if (planData.value?.data) {
        const fresh = countById.get(planData.value.data.id);
        if (fresh !== undefined) planData.value.data.member_count = fresh;
      }
      // Tarifs frères (compteurs « X places restantes » par tarif)
      if (siblingData.value?.data) {
        for (const sibling of siblingData.value.data) {
          const fresh = countById.get(sibling.id);
          if (fresh !== undefined) sibling.member_count = fresh;
        }
      }
    });
  });
  // (Déconnexion/leave gérés par le onUnmounted interne de useMembershipSocket.)

  // Await SSR (cf. note en tête de composable) : bloque le setup jusqu'à la
  // résolution du fetch principal — le fetch siblings est `lazy` (non
  // bloquant), comme dans l'ancien code inline de la page.
  await planAsync;
  await siblingAsync;

  return {
    plan,
    planOptions,
    customFields,
    isInitialLoading,
    isEntityMissing,
    missingCopy,
    allPlans,
    hasMultiplePlans,
    groupTitle,
    isAllPlansClosed,
    isAllSeasonsEnded,
    getPlanOptions,
    getPlanCustomFields,
    prorateInfo,
    hasProrateDiscount,
    getPlanPrice,
    getPlanTitle,
  };
}
